/**
 * Main application component for Västtrafik Tracker.
 */

import { useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';

import { queryClient } from './services/api';
import { useWebSocket } from './hooks/useWebSocket';
import { useVehicleState } from './hooks/useVehicleState';
import { useIsMobile } from './hooks/useIsMobile';
import { useSettingsStore } from './stores/settingsStore';
import { useResolvedTheme } from './hooks/useResolvedTheme';
import { useConfigStore } from './stores/configStore';
import { useVehicleStore } from './stores/vehicleStore';
import { MapContainer } from './components/Map/MapContainer';
import { FilterPanel } from './components/Controls/FilterPanel';
import { SettingsPanel } from './components/Controls/SettingsPanel';
import { ConnectionStatus } from './components/Controls/ConnectionStatus';
import { SearchInput } from './components/Controls/SearchInput';
import { ActiveFilters } from './components/Controls/ActiveFilters';
import { ConfigDrawer } from './components/Controls/ConfigDrawer';
import { JourneyInfoPanel } from './components/Panels/JourneyInfoPanel';
import { SchedulePanel } from './components/Panels/SchedulePanel';
import { VehicleTable } from './components/Accessibility/VehicleTable';
import { AuthProvider, UserMenu } from './components/Auth';
import { useAuthStore } from './stores/authStore';
import { DebugBadge } from './components/Controls/DebugBadge';
import { DebugTrace } from './pages/DebugTrace';
import { PerformanceStats } from './components/Debug';
import { BottomSheet, MobileVehicleList } from './components/Mobile';

/**
 * Hook to apply theme class to document root.
 */
function useThemeEffect() {
  const theme = useSettingsStore((state) => state.theme);

  useEffect(() => {
    const root = document.documentElement;

    const applyTheme = (isDark: boolean) => {
      root.classList.toggle('dark', isDark);
      root.style.colorScheme = isDark ? 'dark' : 'light';
    };

    if (theme === 'dark') {
      applyTheme(true);
    } else if (theme === 'light') {
      applyTheme(false);
    } else {
      // System preference
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      applyTheme(mediaQuery.matches);

      const handler = (e: MediaQueryListEvent) => applyTheme(e.matches);
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [theme]);
}

function AppContent() {
  // Apply theme to document root
  useThemeEffect();

  // Initialize WebSocket connection
  useWebSocket({ autoConnect: true });

  // Initialize vehicle state management (cleanup, interpolation)
  useVehicleState({ enableCleanup: true });

  // Mobile detection
  const isMobile = useIsMobile();

  // Mobile bottom sheet states
  const [controlsSheetOpen, setControlsSheetOpen] = useState(false);

  // Get view mode
  const viewMode = useSettingsStore((state) => state.viewMode);
  const toggleViewMode = useSettingsStore((state) => state.toggleViewMode);

  // Config drawer
  const openConfigDrawer = useConfigStore((state) => state.openDrawer);

  // Get user role for admin check
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';

  // Vehicle selection for mobile journey sheet
  const selectedVehicleId = useVehicleStore((state) => state.selectedVehicleId);
  const selectVehicle = useVehicleStore((state) => state.selectVehicle);

  // Journey sheet is open when a vehicle is selected (derived state)
  const journeySheetOpen = isMobile && !!selectedVehicleId;

  // Close journey sheet by clearing vehicle selection
  const handleJourneySheetClose = () => {
    selectVehicle(null);
  };

  return (
    <div className="h-full flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Skip link for accessibility */}
      <a
        href="#main-content"
        className="skip-link"
      >
        Skip to main content
      </a>

      {/* Header bar */}
      <header className="bg-white dark:bg-gray-800 shadow-sm z-10 px-3 md:px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 md:gap-4">
          <h1 className="text-base md:text-lg font-semibold text-gray-800 dark:text-gray-100">
            {isMobile ? 'VT Tracker' : 'Västtrafik Tracker'}
          </h1>
          {!isMobile && isAdmin && <DebugBadge />}
          <ConnectionStatus />
          <ThemeToggle />
        </div>

        {/* Config menu (center) - admin only, desktop only */}
        {isAdmin && !isMobile && (
          <button
            onClick={openConfigDrawer}
            className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Open configuration"
            title="Configuration"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        {/* View mode toggle and user menu */}
        <div className="flex items-center gap-2 md:gap-4">
          {/* View mode toggle - larger touch targets on mobile */}
          <div className="flex items-center gap-1 md:gap-2">
            {!isMobile && <span className="text-sm text-gray-600 dark:text-gray-300">View:</span>}
            <button
              onClick={toggleViewMode}
              className={`px-3 py-2 md:py-1 rounded text-sm font-medium transition-colors min-h-[44px] md:min-h-0 ${
                viewMode === 'map'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
              aria-pressed={viewMode === 'map'}
            >
              Map
            </button>
            <button
              onClick={toggleViewMode}
              className={`px-3 py-2 md:py-1 rounded text-sm font-medium transition-colors min-h-[44px] md:min-h-0 ${
                viewMode === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
              aria-pressed={viewMode === 'table'}
            >
              List
            </button>
          </div>
          <UserMenu />
        </div>
      </header>

      {/* Config Drawer - desktop only */}
      {!isMobile && <ConfigDrawer />}

      {/* Main content */}
      <main
        id="main-content"
        className="flex-1 relative overflow-hidden"
      >
        {viewMode === 'map' ? (
          <>
            {/* Map view */}
            <MapContainer className="absolute inset-0" />

            {isMobile ? (
              /* Mobile: FAB button to open controls */
              <>
                <button
                  onClick={() => setControlsSheetOpen(true)}
                  className="fixed bottom-20 right-4 z-30 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center active:bg-blue-700 transition-colors"
                  aria-label="Open filters and settings"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </button>

                {/* Mobile controls bottom sheet */}
                <BottomSheet
                  isOpen={controlsSheetOpen}
                  onClose={() => setControlsSheetOpen(false)}
                  title="Filters & Settings"
                  height="70vh"
                >
                  <div className="p-4 space-y-4">
                    <SearchInput placeholder="Search by line (e.g. 9, 52)..." />
                    <FilterPanel />
                    <ActiveFilters />
                    {isAdmin && <SettingsPanel />}
                  </div>
                </BottomSheet>

                {/* Mobile journey info + schedule bottom sheet */}
                <BottomSheet
                  isOpen={journeySheetOpen}
                  onClose={handleJourneySheetClose}
                  title="Journey Details"
                  height="75vh"
                >
                  <JourneyInfoPanel embedded />
                  <SchedulePanel embedded />
                </BottomSheet>
              </>
            ) : (
              /* Desktop: Control panels overlay */
              <>
                <div className="absolute top-4 left-4 space-y-3 z-10 max-w-xs">
                  <SearchInput placeholder="Search by line (e.g. 9, 52)..." />
                  <FilterPanel />
                  <ActiveFilters compact />
                  {isAdmin && <SettingsPanel />}
                </div>

                {/* Desktop journey info + schedule panels in shared fixed container */}
                <div
                  className={`
                    fixed right-4 top-20 w-80 z-10
                    max-h-[calc(100vh-6rem)] overflow-y-auto
                    flex flex-col gap-2
                    transform transition-transform duration-300 ease-out
                    ${selectedVehicleId ? 'translate-x-0' : 'translate-x-full'}
                  `}
                >
                  <JourneyInfoPanel embedded />
                  <SchedulePanel embedded />
                </div>
              </>
            )}
          </>
        ) : (
          /* Table/List view */
          isMobile ? (
            /* Mobile: Simplified list view */
            <MobileVehicleList className="h-full" />
          ) : (
            /* Desktop: Full table view */
            <div className="h-full flex flex-col bg-white dark:bg-gray-800">
              {/* Filter controls for table view */}
              <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
                <div className="flex flex-wrap gap-3">
                  <SearchInput className="flex-1 min-w-[200px]" placeholder="Search by line (e.g. 9, 52)..." />
                  <FilterPanel className="flex-1 min-w-[200px]" />
                  <ActiveFilters className="flex-1 min-w-[200px]" />
                </div>
              </div>
              {/* Table with scroll */}
              <div className="flex-1 overflow-auto">
                <VehicleTable />
              </div>
            </div>
          )
        )}
      </main>

      {/* Debug performance stats overlay - desktop only */}
      {!isMobile && isAdmin && <PerformanceStats />}

      {/* Footer - vehicle count */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-1 text-xs text-gray-500 dark:text-gray-400">
        <VehicleCount />
      </footer>
    </div>
  );
}

function ThemeToggle() {
  const resolvedTheme = useResolvedTheme();
  const setTheme = useSettingsStore((state) => state.setTheme);

  const toggle = () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');

  return (
    <button
      onClick={toggle}
      className="p-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
      aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
      title={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {resolvedTheme === 'dark' ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
}

function VehicleCount() {
  const { vehicleCount } = useVehicleState();
  return (
    <span>
      {vehicleCount} vehicle{vehicleCount !== 1 ? 's' : ''} tracking
    </span>
  );
}

/**
 * Selects between the debug trace page and the main app based on URL params.
 * Must be inside QueryClientProvider + AuthProvider so auth context is available.
 */
function AppRouter() {
  const user = useAuthStore((state) => state.user);
  const urlParams = new URLSearchParams(window.location.search);
  const debugMode = urlParams.has('debug');

  if (debugMode && user?.role === 'admin') {
    return <DebugTrace />;
  }

  return <AppContent />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
